import React from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import styles from "./Pagination.module.scss";

/**
 * Pagination — design-system primitive (DESIGN.md §2 pagination sözleşmesi).
 *
 * Eski `Components/Tables/DataTable/Pagination.jsx` (Pager) ile API uyumlu;
 * drop-in replacement. Reactstrap'a bağımlı değil — token-driven, dark-mode
 * otomatik, focus-ring + a11y label'lar dahil.
 *
 * Props:
 *   - page, pageSize, total            (zorunlu)
 *   - onChange(page)                   (zorunlu)
 *   - onPageSizeChange(size)?          (opsiyonel; rightOnly=false ise gösterilir)
 *   - pageSizes?: number[]             (default [10,25,50,100])
 *   - windowSize?: number              (görünür sayfa pencere boyutu, default 5)
 *   - rightOnly?: boolean              (true: sadece sayfa butonları sağda)
 *   - hideSummary?: boolean            (sol ve right'a "1-10 / 50" yazısını gizle)
 *   - className?: string
 */
const Pagination = ({
  page,
  pageSize,
  total,
  onChange,
  onPageSizeChange,
  pageSizes = [10, 25, 50, 100],
  windowSize = 5,
  rightOnly = false,
  hideSummary = false,
  className = "",
}) => {
  const totalPages = Math.max(1, Math.ceil((total || 0) / Math.max(1, pageSize || 1)));
  const safePage = Math.min(Math.max(1, page || 1), totalPages);
  const go = (p) => onChange?.(Math.min(Math.max(1, p), totalPages));

  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, total);

  // Görünür sayfa penceresi
  const left = Math.max(1, safePage - Math.floor(windowSize / 2));
  const right = Math.min(totalPages, Math.max(left + windowSize - 1, windowSize));
  const visible = [];
  for (let p = left; p <= right; p++) visible.push(p);

  const showLeftEllipsis = left > 2;
  const showRightEllipsis = right < totalPages - 1;
  const showFirst = left > 1;
  const showLast = right < totalPages;

  return (
    <nav
      className={`${styles.root} ${rightOnly ? styles.rightOnly : ""} ${className}`}
      aria-label="Sayfa gezinme"
    >
      {!rightOnly && (
        <div className={styles.left}>
          {onPageSizeChange && pageSizes.length > 1 && (
            <span className={styles.pageSize}>
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
                aria-label="Sayfa boyutu"
              >
                {pageSizes.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </span>
          )}
          {!hideSummary && (
            <span className={styles.summary}>
              {total === 0 ? "0 / 0" : `${start}–${end} / ${total}`}
            </span>
          )}
        </div>
      )}

      <div className={styles.pages}>
        <button
          type="button"
          className={`${styles.btn} ${styles.iconBtn}`}
          onClick={() => go(1)}
          disabled={safePage <= 1}
          aria-label="İlk sayfa"
        >
          <ChevronsLeft size={16} />
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.iconBtn}`}
          onClick={() => go(safePage - 1)}
          disabled={safePage <= 1}
          aria-label="Önceki sayfa"
        >
          <ChevronLeft size={16} />
        </button>

        {showFirst && (
          <>
            <button
              type="button"
              className={styles.btn}
              onClick={() => go(1)}
              aria-label="Sayfa 1"
            >
              1
            </button>
            {showLeftEllipsis && <span className={styles.ellipsis}>…</span>}
          </>
        )}

        {visible.map((p) => {
          const active = p === safePage;
          return (
            <button
              key={p}
              type="button"
              className={`${styles.btn} ${active ? styles.active : ""}`}
              onClick={() => go(p)}
              aria-current={active ? "page" : undefined}
              aria-label={`Sayfa ${p}`}
            >
              {p}
            </button>
          );
        })}

        {showLast && (
          <>
            {showRightEllipsis && <span className={styles.ellipsis}>…</span>}
            <button
              type="button"
              className={styles.btn}
              onClick={() => go(totalPages)}
              aria-label={`Sayfa ${totalPages}`}
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          type="button"
          className={`${styles.btn} ${styles.iconBtn}`}
          onClick={() => go(safePage + 1)}
          disabled={safePage >= totalPages}
          aria-label="Sonraki sayfa"
        >
          <ChevronRight size={16} />
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.iconBtn}`}
          onClick={() => go(totalPages)}
          disabled={safePage >= totalPages}
          aria-label="Son sayfa"
        >
          <ChevronsRight size={16} />
        </button>
      </div>
    </nav>
  );
};

export default Pagination;
